import type { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { faFileAlt, faMinusCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { AxiosProgressEvent } from 'axios';
import type { FieldInputProps } from 'formik';
import { useFormikContext } from 'formik';
import { del, get, set } from 'idb-keyval';
import type {
  ComponentPropsWithRef,
  CSSProperties,
  FC,
  ReactNode,
} from 'react';
import { useCallback, useEffect, useMemo, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import type {
  DropzoneInputProps,
  DropzoneRootProps,
  FileRejection,
} from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import mergeRefs from 'react-merge-refs';
import { toast } from 'sonner';
import urlTemplate from 'url-template';
import { z } from 'zod';
import type { Link } from 'src/apis/api-utilities';
import { Button } from 'src/components/buttons-and-actions/button';
import { ErrorMessage, ErrorText } from 'src/components/form/error-message';
import { FieldLayout } from 'src/components/form/field-layout';
import type { FormFieldProps } from 'src/components/form/form-field-props';
import { FormHint } from 'src/components/form/form-hint';
import { FieldLabel } from 'src/components/form/label';
import {
  borderRadius,
  borderWidth,
  placeholderLikeClasses,
} from 'src/components/form/styles';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { requiredOutput } from 'src/components/form/zod-utilities';
import { ExternalLink } from 'src/components/navigation/link';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useAxios } from 'src/hooks/use-axios';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { useDropManager } from 'src/hooks/use-drop-manager';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { Colors } from 'src/styles';
import { cn } from 'src/utils/cn';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';
import { setLocationHref } from 'src/utils/location-usage';

export const nullableFileSchema = z
  .object({
    fileName: z.string(),
    id: z.string(),
  })
  .nullable();

export const fileSchema = nullableFileSchema.transform(
  requiredOutput({ message: 'Please upload a file for {label}.' })
);

const FileCard: FC<{
  hasError?: boolean;
  disabled?: boolean;
  children: ReactNode;
}> = ({ hasError, disabled, children }) => {
  return (
    <div
      className={cn(
        `
          rounded-xs border border-border-surface-default px-xl py-2xl
          shadow-[0_0.1rem_0.1rem_rgba(0,0,0,0.05)]
          legacy:rounded-[0.188rem] legacy:border-legacy-brand-light-3
          legacy:p-[1.5rem]
        `,
        hasError &&
          `
            border-border-support-error-subdued
            bg-background-support-error-minimal text-foreground-support-error
            legacy:border-legacy-error legacy:bg-legacy-error-lighter
          `,
        disabled &&
          `
            border-border-surface-disabled bg-background-surface-disabled
            not-legacy:text-foreground-surface-disabled
            legacy:border-legacy-inactive legacy:bg-legacy-inactive-lighter
          `
      )}
    >
      {children}
    </div>
  );
};

const borderRadiusSizes = {
  'small-dashes': borderRadius,
  'medium-dashes': '0.376rem',
  'large-dashes': '0.752rem',
};

const borderRadiusSizesNew = {
  'small-dashes': '--radius-xs',
  'medium-dashes': '--radius-xs',
  'large-dashes': '--radius-xs',
};

const strokeWidthSizes = {
  'small-dashes': borderWidth,
  'medium-dashes': '0.4rem',
  'large-dashes': '0.8rem',
};

const dashArraySizes = {
  'small-dashes': '5',
  'medium-dashes': '10',
  'large-dashes': '20',
};

const globalStyle = getComputedStyle(document.documentElement);

// dashed borders can only be customized by faking them with an svg background
const dashedBorder = ({
  hasError,
  disabled,
  type,
  isNewDesignActive,
}: {
  hasError?: boolean;
  disabled?: boolean;
  isNewDesignActive: boolean;
  type: 'small-dashes' | 'medium-dashes' | 'large-dashes';
}) => {
  const fill = isNewDesignActive
    ? disabled
      ? globalStyle.getPropertyValue('--color-background-surface-disabled')
      : 'none'
    : disabled
      ? Colors.inactiveLighter
      : 'none';
  const stroke = isNewDesignActive
    ? disabled
      ? globalStyle.getPropertyValue('--color-border-surface-disabled')
      : hasError
        ? globalStyle.getPropertyValue('--color-border-support-error-subdued')
        : globalStyle.getPropertyValue('--color-border-surface-default')
    : disabled
      ? Colors.inactive
      : hasError
        ? Colors.error
        : Colors.brand;
  const radius = isNewDesignActive
    ? globalStyle.getPropertyValue(borderRadiusSizesNew[type])
    : borderRadiusSizes[type];
  return `<svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
  <rect
    width="100%"
    height="100%"
    fill="${fill}"
    rx="${radius}"
    ry="${radius}"
    stroke="${stroke}"
    stroke-width="${strokeWidthSizes[type]}"
    stroke-dasharray="${dashArraySizes[type]}"
    stroke-linecap="butt"
  />
</svg>`;
};

export const DropContainer: FC<
  {
    hasError: boolean;
    disabled?: boolean;
  } & ComponentPropsWithRef<'div'>
> = ({ hasError, disabled, ...props }) => {
  const isNewDesignActive = useIsNewDesignActive();
  const backgroundImage = useMemo(
    () =>
      `url('data:image/svg+xml;base64,${window.btoa(dashedBorder({ hasError, disabled, isNewDesignActive, type: 'small-dashes' }))}')`,
    [hasError, disabled, isNewDesignActive]
  );
  return (
    <div
      {...props}
      style={
        {
          '--drop-container-background-image': backgroundImage,
          ...props.style,
        } as CSSProperties
      }
      className={cn(
        placeholderLikeClasses,
        `
          cursor-pointer rounded-xs bg-(image:--drop-container-background-image)
          bg-clip-padding px-xl py-2xl select-none
          legacy:rounded-[0.188rem] legacy:px-[2rem] legacy:py-[1.35rem]
          legacy:text-legacy-xs
        `,
        hasError &&
          `
            border-border-support-error-subdued
            bg-background-support-error-minimal text-foreground-support-error
            legacy:border-legacy-error legacy:bg-legacy-error-lighter
            legacy:text-legacy-error
          `,
        disabled &&
          `
            cursor-not-allowed border-border-surface-disabled
            bg-background-surface-disabled text-foreground-surface-disabled
            legacy:border-legacy-inactive legacy:bg-legacy-inactive-lighter
            legacy:text-legacy-inactive
          `,
        props.className
      )}
    />
  );
};

const dropAreaClasses = cn(`
  grid items-center justify-center bg-background-surface-hover/80 p-md
  text-body-lg font-legacy-bold text-foreground-surface-default not-italic
  legacy:bg-legacy-brand-light-3/60 legacy:text-[2rem] legacy:font-legacy-bold
  legacy:text-legacy-brand
`);

export const LargeDropArea: FC<ComponentPropsWithRef<'div'>> = (props) => {
  const isNewDesignActive = useIsNewDesignActive();
  const backgroundImage = useMemo(
    () =>
      `url('data:image/svg+xml;base64,${window.btoa(dashedBorder({ isNewDesignActive, type: isNewDesignActive ? 'small-dashes' : 'large-dashes' }))}')`,
    [isNewDesignActive]
  );
  return (
    <div
      {...props}
      style={
        {
          '--drop-area-background-image': backgroundImage,
          ...props.style,
        } as CSSProperties
      }
      className={cn(
        dropAreaClasses,
        `
          fixed inset-[1.5rem] z-4 rounded-md
          bg-(image:--drop-area-background-image)
        `
      )}
    />
  );
};

export const SmallDropArea: FC<ComponentPropsWithRef<'div'>> = (props) => {
  const isNewDesignActive = useIsNewDesignActive();
  const backgroundImage = useMemo(
    () =>
      `url('data:image/svg+xml;base64,${window.btoa(dashedBorder({ isNewDesignActive, type: isNewDesignActive ? 'small-dashes' : 'medium-dashes' }))}')`,
    [isNewDesignActive]
  );
  return (
    <div
      {...props}
      style={
        {
          '--drop-area-background-image': backgroundImage,
          ...props.style,
        } as CSSProperties
      }
      className={cn(
        dropAreaClasses,
        `
          absolute top-0 right-0 z-1 size-full rounded-xs
          bg-(image:--drop-area-background-image)
          not-legacy:bg-background-surface-subdued
          legacy:top-[-2rem] legacy:right-[-2rem] legacy:size-[calc(100%+4rem)]
        `
      )}
    />
  );
};

type SimpleFileUploadProps = {
  id: string;
  name: string;
  progress: number;
  file: File | null;
  fileField: FieldInputProps<FileValue | null>;
  onRemove: () => void;
  previewImageUrl?: string;
  getDownloadUrl?: string;
  s3Download: ReturnType<typeof useS3Download>;
  s3Upload: ReturnType<typeof useS3Upload>;
  getRootProps(props?: DropzoneRootProps): DropzoneRootProps;
  getInputProps(props?: DropzoneInputProps): DropzoneInputProps;
  hasError: boolean;
  formats: string;
  limit: string;
  localPreview?: boolean;
  disabled?: boolean;
  icon: IconDefinition;
};

const SimpleFileUpload: FC<SimpleFileUploadProps> = ({
  id,
  name,
  fileField,
  progress,
  file,
  onRemove,
  getDownloadUrl,
  previewImageUrl,
  s3Download,
  s3Upload,
  getRootProps,
  getInputProps,
  hasError,
  formats,
  limit,
  localPreview,
  disabled,
  icon,
}) => {
  const [previewPending, setPreviewPending] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewAvailable, setIsPreviewAvailable] = useState<boolean>(true);

  const dropManager = useDropManager();
  const focusRef = useFocusManager({ name, type: 'click' });

  useEffect(() => {
    if (fileField.value && localPreview && isPreviewAvailable) {
      setPreviewPending(true);
      get<File>(fileField.value.id)
        .then((file) => {
          if (!file) throw 'No preview possible';
          // revoke previously created object url, if a new one is set
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewUrl(URL.createObjectURL(file));
          setPreviewPending(false);
        })
        .catch(() => {
          setIsPreviewAvailable(false);
        });
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [localPreview, fileField.value]);

  // TODO: this is a workaround to differentiate the _initial_ image vs a changed image
  const isInitialImage =
    fileField.value && fileField.value.id === fileField.value.fileName;

  useEffect(() => {
    if (fileField.value && file && previewImageUrl && !isInitialImage) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(file));
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [previewImageUrl, file, isInitialImage, fileField.value]);

  useEffect(() => {
    return () => {
      // revoke previously created object url, on unmount
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  const isNewDesignActive = useIsNewDesignActive();

  if (fileField.value) {
    return (
      <FileCard hasError={hasError} disabled={disabled}>
        <div
          className={cn(
            `
              grid grid-cols-[auto_1fr_auto] items-center gap-lg
              legacy:gap-x-[1rem] legacy:gap-y-[0.5rem]
            `,
            previewImageUrl ? 'legacy:items-start' : 'legacy:items-baseline'
          )}
        >
          {!previewImageUrl && (
            <FontAwesomeIcon
              icon={icon}
              color={
                disabled ? Colors.inactive : hasError ? Colors.error : undefined
              }
            />
          )}

          {/* 1) either just file name */}
          {!(localPreview && isPreviewAvailable) &&
            !getDownloadUrl &&
            (previewImageUrl && isInitialImage ? (
              <span
                className={cn(hasError && 'legacy:text-legacy-error')}
                data-testid={`${name}.fileName`}
              >
                <img
                  className="max-h-[300px] max-w-full"
                  src={previewImageUrl}
                />
              </span>
            ) : previewImageUrl && previewUrl ? (
              <span
                className={cn(hasError && 'legacy:text-legacy-error')}
                data-testid={`${name}.fileName`}
              >
                <img className="max-h-[300px] max-w-full" src={previewUrl} />
              </span>
            ) : (
              <span
                className={cn(hasError && 'legacy:text-legacy-error')}
                data-testid={`${name}.fileName`}
              >
                {fileField.value.fileName}
              </span>
            ))}

          {/* 2) or local preview */}
          {localPreview &&
            isPreviewAvailable &&
            (previewPending ? (
              <Button mode="link" disabled>
                Preparing...
              </Button>
            ) : (
              <ExternalLink
                href={previewUrl!}
                download={fileField.value.fileName}
                className={cn(
                  'legacy:underline',
                  hasError && 'legacy:text-legacy-error'
                )}
                data-testid={`${name}.fileName`}
              >
                {fileField.value.fileName}
              </ExternalLink>
            ))}

          {/* 3) or download */}
          {getDownloadUrl && (
            <Button
              mode="link"
              onClick={() =>
                s3Download.execute({
                  url: getDownloadUrl,
                  id: fileField.value!.id,
                })
              }
              disabled={s3Download.pending}
              className={cn(
                'legacy:underline',
                hasError && 'legacy:text-legacy-error'
              )}
              data-testid={`${name}.fileName`}
            >
              {s3Download.pending ? 'Preparing...' : fileField.value.fileName}
            </Button>
          )}

          <Button
            mode="icon"
            onClick={onRemove}
            // ignore - just legacy styles that will be deleted in some months
            // oxlint-disable-next-line prisma/tailwind-no-inline-styles
            style={
              !isNewDesignActive
                ? {
                    display: 'grid',
                    borderBottom: 'none',
                    color: disabled
                      ? Colors.inactive
                      : hasError
                        ? Colors.error
                        : undefined,
                  }
                : undefined
            }
            className={cn(
              !disabled && hasError && 'text-foreground-support-error'
            )}
            aria-label="Remove File"
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faMinusCircle} />
          </Button>
        </div>
      </FileCard>
    );
  }

  if (s3Upload.pending || s3Upload.response) {
    return (
      <FileCard>
        <div
          className={`
            grid grid-cols-[auto_1fr_auto] items-center gap-lg
            legacy:items-baseline legacy:gap-x-[1rem] legacy:gap-y-[0.5rem]
          `}
        >
          <FontAwesomeIcon
            icon={icon}
            className={`
              text-foreground-surface-subdued
              legacy:text-legacy-brand-light-2
            `}
          />
          <span
            className={`
              text-foreground-surface-subdued
              legacy:text-legacy-brand-light-2
            `}
          >
            {file!.name}
          </span>
          <Button
            mode="icon"
            disabled={!s3Upload.pending}
            onClick={() => {
              if (s3Upload.pending) s3Upload.canceler();
            }}
            className="grid border-b-0"
            aria-label="Cancel Upload"
          >
            <FontAwesomeIcon icon={faMinusCircle} />
          </Button>

          <span />

          <div
            className={`
              h-[0.3rem] rounded-2xs bg-background-surface-subdued
              legacy:bg-legacy-brand-light-4
            `}
          >
            <div
              style={
                {
                  '--progress-width': `${progress * 100}%`,
                } as CSSProperties
              }
              className={`
                h-full w-(--progress-width) rounded-2xs
                bg-background-surface-active
                [transition:width_200ms_cubic-bezier(0,1,1,1)]
                legacy:bg-legacy-brand
              `}
            />
            <span />
          </div>
        </div>
      </FileCard>
    );
  }

  const showLargeDropArea =
    dropManager.hasActiveDrag && dropManager.onlyDropTarget;

  const showSmallDropArea =
    dropManager.hasActiveDrag &&
    !dropManager.onlyDropTarget &&
    dropManager.isClosestDropTarget;

  return (
    <DropContainer
      {...getRootProps()}
      hasError={Boolean(hasError)}
      disabled={disabled}
      ref={mergeRefs([focusRef, dropManager.dropTargetRef])}
      className="relative" // needed for small drop area
    >
      {showLargeDropArea &&
        createPortal(
          <LargeDropArea data-testid={`${name}.largeDropArea`}>
            <p>Drop Your Files Here</p>
          </LargeDropArea>,
          document.body
        )}

      {showSmallDropArea && (
        <SmallDropArea data-testid={`${name}.smallDropArea`}>
          <p>Drop Your Files Here</p>
        </SmallDropArea>
      )}

      <input
        id={id}
        {...getInputProps()}
        data-testid={name}
        disabled={disabled}
      />

      <div
        className={`
          grid items-center justify-center gap-lg
          not-legacy:grid-cols-[auto_1fr]
          legacy:grid-flow-col legacy:gap-[1rem]
        `}
      >
        <FontAwesomeIcon icon={icon} size={isNewDesignActive ? 'lg' : '2x'} />
        <div>
          <p>
            Drop your file here or <span className="underline">select one</span>{' '}
            from your computer.
          </p>
          <p>
            We accept {formats} files that are no larger than {limit}.
          </p>
        </div>
      </div>
    </DropContainer>
  );
};

export type GetDownloadUrlResponse = {
  // a download URL pointing to S3
  downloadUrl: string;
  fileName: string;
  /** @deprecated only for shipper registration */
  templateId?: string;
  id: string;
};

type GetDownloadUrlParams = {
  // a URL template point to one of our services
  // -> that one will give us a download URL pointing to S3 back
  url: string;
  id: string;
};

export function useS3Download() {
  const getDownloadUrl = useAxios(
    (axios, baseConfig, { url, id }: GetDownloadUrlParams) =>
      axios.request<GetDownloadUrlResponse>({
        ...baseConfig,
        url: urlTemplate.parse(url).expand({ id }),
        method: 'GET',
      }),
    { neededOnPageLoad: false }
  );

  // useEffect(() => {
  //   if (getDownloadUrl.error)
  //     toast.error('An unknown error happened.');
  // }, [getDownloadUrl.error]);

  return getDownloadUrl;
}

export type GetUploadUrlResponse = {
  uploadUrl: string;
  /** @deprecated only for shipper registration */
  templateId?: string;
  id: string;
  uploadHeaders: { [header: string]: string };
  // self link is only available, if the file can be previewed
  _links?: {
    self: Link;
  };
};

type S3UploadReturnValue = {
  id: string;
  fileName: string;
  file: File;
};

export function useS3Upload(url: string) {
  const createUploadUrl = useAxios(
    async (
      axios,
      baseConfig,
      file: File,
      onUploadProgress: (event: AxiosProgressEvent) => void
    ) => {
      const template = await axios.request<GetUploadUrlResponse>({
        ...baseConfig,
        url,
        method: 'POST',
        data: {
          fileName: file.name,
          fileSize: file.size,
        },
      });

      // this will be set automatically by the browser (handle different casing variants)
      delete template.data.uploadHeaders['Content-Length'];
      delete template.data.uploadHeaders['content-length'];

      await axios.request({
        ...baseConfig,
        url: template.data.uploadUrl,
        method: 'PUT',
        data: file,
        headers: template.data.uploadHeaders,
        validateStatus: (status) => status < 400,
        onUploadProgress,
      });

      const success: S3UploadReturnValue = {
        id:
          /** @deprecated First check for templateId for shipper registration */
          template.data.templateId || template.data.id,
        fileName: file.name,
        file,
      };

      return success;
    },
    {
      neededOnPageLoad: false,
      onError(error) {
        if (isConstraintViolation(error)) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (createUploadUrl.response) toast.success('Uploaded file.');
  }, [createUploadUrl.response]);

  return createUploadUrl;
}

export type FileValue = {
  id: string;
  fileName: string;
};

export type FileUploadProps = {
  getUploadUrl: string;
  /**
   * This file will trigger an immediate upload.
   */
  initialFile?: File;
  hideLabel?: boolean;
  stacked?: boolean;
  icon?: IconDefinition;
  // currently these settings need to be hard-coded at the frontend
  limit?: string;
  formats?: string;
  accept?: string;
} & (
  | {
      getDownloadUrl?: string;
      localPreview?: undefined;
      previewImageUrl?: undefined;
    }
  | {
      getDownloadUrl?: undefined;
      /**
       * Please be extremely cautious when using this option. It was only introduced for
       * the registration case where you have non-public files and don't have an
       * authenticated user.
       * If you're in a context with an authenticated user the backend should always
       * offer a `getDownloadUrl` endpoint. Otherwise _other_ users can't download the file
       * (e.g. in an edit context where you edit what someone else uploaded as the preview is only
       * stored locally for the one that uploaded it).
       * *If* you really have to use it, make sure that you also clean up what you stored in the
       * in the IndexedDB at some point in time to not pollute the customer disk space.
       */
      localPreview?: boolean;
      previewImageUrl?: undefined;
    }
  | {
      getDownloadUrl?: undefined;
      localPreview?: boolean;
      previewImageUrl?: string;
    }
) &
  FormFieldProps;

/**
 * The file upload can by used for any kind of file upload. In case it is an image a preview of this
 * can be shown inline.
 */
export const FileUpload: FC<FileUploadProps> = (props) => {
  const { isStacked } = useDefaultStacked();
  const {
    name,
    getUploadUrl,
    getDownloadUrl,
    initialFile,
    label,
    stacked = isStacked,
    disabled,
    disabledMessage,
    icon = faFileAlt,
    hideLabel = false,
    localPreview = false,
    limit = '25 MB',
    formats = 'tiff, jpg or pdf',
    accept = '.jpeg,.jpg,.pdf,.tiff',
    hint,
    info,
    previewImageUrl,
    markAsRequired,
  } = props;
  const s3Download = useS3Download();
  const s3Upload = useS3Upload(getUploadUrl);

  const { setFieldValue } = useFormikContext<any>();

  const [progress, setProgress] = useState(0);

  const [fileField, showError, error] = useCustomField<FileValue | null>(
    name,
    label
  );

  const [isPreviewAvailable, setIsPreviewAvailable] = useState<boolean>(true);
  const [file, setFile] = useState<File | null>(null);

  const onDropAccepted = useCallback(
    (files: File[]) => {
      const [file] = files;
      setFile(file);
      setRejectedFile(null);
      setProgress(0);
      s3Upload.execute(file, (event) =>
        setProgress(event.loaded / event.total!)
      );
    },
    // generate callback only once - onUpload stays the same
    // oxlint-disable-next-line exhaustive-deps
    []
  );
  useEffect(() => {
    if (initialFile) {
      onDropAccepted([initialFile]);
    }
  }, [initialFile, onDropAccepted]);

  const [rejectedFile, setRejectedFile] = useState<File | null>(null);
  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    const [fileRejection] = fileRejections;
    setRejectedFile(fileRejection.file);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept,
    disabled,
    // backend runs a file size check
    // maxSize: 26214400
  });

  useEffect(() => {
    if (!s3Upload.response) return;

    // if we use a localPreview we need to await saving the file locally, before moving on
    // or could end up in a race condition where we already try to restore the locally saved
    // file, before it was actually saved
    const handleResponse = async (success: S3UploadReturnValue) => {
      const { id, fileName, file } = success;
      try {
        if (localPreview) await set(id, file);
      } catch (error) {
        setIsPreviewAvailable(false);
      }

      setFieldValue(fileField.name, { id, fileName });
    };
    handleResponse(s3Upload.response);
  }, [
    s3Upload.response,
    setFieldValue,
    fileField.name,
    localPreview,
    setIsPreviewAvailable,
  ]);

  useEffect(() => {
    if (s3Download.response) {
      setLocationHref(s3Download.response.data.downloadUrl);
    }
  }, [s3Download.response]);

  // In case of a rejectedFile (client-side) we ignore older server errors reported by a previous upload,
  // useMemo needed there otherwise useSurvey causes rerender and an infinite loop of createUploadUrl requests with 400 error.
  const serverErrors = useMemo(
    () =>
      !rejectedFile && !s3Upload.pending && s3Upload.error && !fileField.value
        ? s3Upload.error.response.data.violations.map(({ field, message }) => {
            const name = file!.name;

            const isSizeError = field === 'fileSize' && message === 'Max';
            const isFormatError =
              field === 'fileName' && message === 'FileName';

            const sizeMessage = `${name} is larger than the ${limit} limit.`;
            const formatMessage = `${name} is an unsupported file format.`;
            const defaultMessage = `An unexpected error happened during the upload. We're sorry. (${field}: ${message})`;

            const error = isSizeError
              ? sizeMessage
              : isFormatError
                ? formatMessage
                : defaultMessage;

            return {
              field,
              label: error,
            };
          })
        : [],
    // oxlint-disable-next-line exhaustive-deps
    [rejectedFile, s3Upload.pending, s3Upload.error, fileField.value, file]
  );
  // If we have a rejected file we currently assume an unsupported file format, because that is the only
  // client-side check we run. Nevertheless the same error can be reported by the browser.
  const clientErrors = rejectedFile
    ? [
        {
          field: fileField.name,
          label: `${rejectedFile.name} is an unsupported file format.`,
        },
      ]
    : [];

  const errors = [...serverErrors, ...clientErrors];

  const [_idField, showIdError, idError] = useCustomField(`${name}.id`, label);

  const [_, showPending, pendingUploadError] = useCustomField(
    `${name}-pending`,
    label,
    {
      validate: () => {
        if (s3Upload.pending)
          return 'File upload in progress, please wait until upload is finished.';
      },
    }
  );

  const fieldId = useId();

  const fileUpload = (
    <div className="grid items-center">
      <Tooltip
        content={disabled && disabledMessage ? disabledMessage : undefined}
      >
        {(targetProps) => (
          <SimpleFileUpload
            id={fieldId}
            name={name}
            previewImageUrl={previewImageUrl}
            getDownloadUrl={getDownloadUrl}
            s3Download={s3Download}
            s3Upload={s3Upload}
            fileField={fileField}
            onRemove={async () => {
              if (localPreview && isPreviewAvailable) {
                try {
                  await del(fileField.value!.id);
                } catch {
                  setIsPreviewAvailable(false);
                }
              }
              setFieldValue(fileField.name, null);
              s3Upload.setResponse(null);
            }}
            file={file}
            progress={progress}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            hasError={
              Boolean(errors.length) ||
              Boolean(showError && error) ||
              Boolean(showIdError && idError)
            }
            formats={formats}
            limit={limit}
            localPreview={localPreview && isPreviewAvailable}
            disabled={disabled}
            icon={icon}
            {...targetProps}
          />
        )}
      </Tooltip>

      {hint && <FormHint isDisabled={props.disabled}>{hint}</FormHint>}

      {errors.map((error) => (
        <ErrorText key={error.field}>{error.label}</ErrorText>
      ))}

      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
        />
      )}
      {showIdError && idError && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={idError}
          label={label}
        />
      )}
      {showPending && pendingUploadError && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={pendingUploadError}
          label={label}
        />
      )}
    </div>
  );

  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;

  return !hideLabel ? (
    <FieldLayout stacked={stacked} className="items-start">
      <FieldLabel
        name={name}
        displayLabel={displayLabel}
        fieldId={fieldId}
        info={info}
        showError={Boolean(errors.length) || showError}
      />

      {fileUpload}
    </FieldLayout>
  ) : (
    fileUpload
  );
};
